import { toRaw, unref } from 'vue'
import { cloneDeep, uniqBy } from 'lodash-es'
import { deepMerge } from '@tav-ui/utils/basic'
import { dateUtil } from '@tav-ui/utils/dateUtil'
import { isArray, isFunction, isNullOrUnDef, isObject, isString } from '@tav-ui/utils/is'
import { error } from '@tav-ui/utils/log'
import { dateItemType, handleInputNumberValue } from '../helper'
import type { NamePath } from 'ant-design-vue/lib/form/interface'
import type { ComputedRef, Ref } from 'vue'
import type { FormActionType, FormEmitType, FormProps, FormSchema } from '../types/form'

type Recordable<T = any> = Record<string, T>
interface Fn<T = any, R = T> {
  (...arg: T[]): R
}
interface UseFormActionContext {
  emit: FormEmitType
  getProps: ComputedRef<FormProps>
  getSchema: ComputedRef<FormSchema[]>
  formModel: Recordable
  defaultValueRef: Ref<Recordable>
  formElRef: Ref<FormActionType>
  schemaRef: Ref<FormSchema[]>
  handleFormValues: Fn
}
export function useFormEvents({
  emit,
  getProps,
  formModel,
  getSchema,
  defaultValueRef,
  formElRef,
  schemaRef,
  handleFormValues,
}: UseFormActionContext) {
  async function resetFields(): Promise<void> {
    const { resetFunc, submitOnReset } = unref(getProps)
    resetFunc && isFunction(resetFunc) && (await resetFunc())

    const formEl = unref(formElRef)
    if (!formEl) return

    Object.keys(formModel).forEach((key) => {
      formModel[key] = defaultValueRef.value[key]
    })
    clearValidate()
    emit('reset', toRaw(formModel))
    submitOnReset && handleSubmit()
  }

  /**
   * @description: Set form value
   */
  async function setFieldsValue(values: Recordable, useValidate = true): Promise<void> {
    const fields = unref(getSchema)
      .map((item) => item.field)
      .filter(Boolean)

    const validKeys: string[] = []
    Object.keys(values).forEach((key) => {
      const schema = unref(getSchema).find((item) => item.field === key)
      let value = values[key]

      const hasKey = Reflect.has(values, key)

      value = handleInputNumberValue(schema?.component, value)
      // 0| '' is allow
      if (hasKey && fields.includes(key)) {
        // time type
        if (itemIsDateType(key)) {
          if (Array.isArray(value)) {
            // const arr: any[] = []
            // for (const ele of value) arr.push(ele ? dateUtil(ele) : null)
            // formModel[key] = arr
            formModel[key] = value
          } else {
            const { componentProps } = schema || {}
            let _props = componentProps as any
            if (typeof componentProps === 'function') _props = _props({ formModel })

            formModel[key] = value ? (_props?.valueFormat ? value : dateUtil(value)) : null
          }
        } else {
          formModel[key] = value
        }
        validKeys.push(key)
      }
    })
    useValidate &&
      validateFields(validKeys).catch((_) => {
        // console.log(_)
      })
  }
  /**
   * @description: Delete based on field name
   */
  async function removeSchemaByFiled(fields: string | string[]): Promise<void> {
    const schemaList: FormSchema[] = cloneDeep(unref(getSchema))
    if (!fields) return

    let fieldList: string[] = isString(fields) ? [fields] : fields
    if (isString(fields)) fieldList = [fields]

    for (const field of fieldList) _removeSchemaByFiled(field, schemaList)

    schemaRef.value = schemaList
  }

  /**
   * @description: Delete based on field name
   */
  function _removeSchemaByFiled(field: string, schemaList: FormSchema[]): void {
    if (isString(field)) {
      const index = schemaList.findIndex((schema) => schema.field === field)
      if (index !== -1) {
        delete formModel[field]
        schemaList.splice(index, 1)
      }
    }
  }

  /**
   * @description: Insert after a certain field, if not insert the last
   */
  async function appendSchemaByField(schema: FormSchema, prefixField?: string, first = false) {
    const schemaList: FormSchema[] = cloneDeep(unref(getSchema))

    const index = schemaList.findIndex((schema) => schema.field === prefixField)

    if (!prefixField || index === -1 || first) {
      first ? schemaList.unshift(schema) : schemaList.push(schema)
      schemaRef.value = schemaList
      return
    }
    if (index !== -1) schemaList.splice(index + 1, 0, schema)

    schemaRef.value = schemaList
  }

  async function resetSchema(data: Partial<FormSchema> | Partial<FormSchema>[]) {
    let updateData: Partial<FormSchema>[] = []
    if (isObject(data)) updateData.push(data as FormSchema)

    if (isArray(data)) updateData = [...data]

    const hasField = updateData.every(
      (item) =>
        item.component === 'Divider' ||
        item.component === 'FormTitle' ||
        (Reflect.has(item, 'field') && item.field)
    )

    if (!hasField) {
      error(
        'All children of the form Schema array that need to be updated must contain the `field` field'
      )
      return
    }
    schemaRef.value = updateData as FormSchema[]
  }

  async function updateSchema(data: Partial<FormSchema> | Partial<FormSchema>[]) {
    let updateData: Partial<FormSchema>[] = []
    if (isObject(data)) {
      updateData.push(data as FormSchema)
    }
    if (isArray(data)) {
      updateData = [...data]
    }

    const hasField = updateData.every(
      (item) =>
        item.component === 'Divider' ||
        item.component === 'FormTitle' ||
        (Reflect.has(item, 'field') && item.field)
    )

    if (!hasField) {
      error(
        'All children of the form Schema array that need to be updated must contain the `field` field'
      )
      return
    }
    const schema: FormSchema[] = unref(getSchema)
    updateData.forEach((item) => {
      // debugger
      const findResult = schema.find((val) => val.field === item.field)
      if (findResult) {
        const newSchema: FormSchema = deepMerge(findResult, item)
        schema.push(newSchema)
      } else {
        schema.push(item as FormSchema)
      }
      // unref(getSchema).forEach((val) => {
      //   if (val.field === item.field) {
      //     const newSchema = deepMerge(val, item);
      //     schema.push(newSchema as FormSchema);
      //   } else {
      //     schema.push(val);
      //   }
      // });
    })
    schemaRef.value = uniqBy(schema, 'field')
  }

  // 转换数据，由于InputNumber现在是string类型，需要转换为number
  function transFormFieldsValue(values: Recordable): Recordable {
    for (let i = 0; i < unref(getSchema).length; i++) {
      const item = unref(getSchema)[i]
      if (item.component === 'InputNumber' && !isNullOrUnDef(values[item.field])) {
        values[item.field] = Number(values[item.field])
      }
    }
    return values
  }

  function getFieldsValue(): Recordable {
    const formEl = unref(formElRef)
    if (!formEl) return {}
    const res = handleFormValues(toRaw(unref(formModel)))
    return transFormFieldsValue(res)
  }

  /**
   * @description: Is it time
   */
  function itemIsDateType(key: string) {
    return unref(getSchema).some((item) => {
      return item.field === key ? dateItemType.includes(item.component as any) : false
    })
  }

  async function validateFields(nameList?: NamePath[] | undefined) {
    return unref(formElRef)?.validateFields(nameList)
  }

  async function validate(nameList?: NamePath[] | undefined) {
    // eslint-disable-next-line no-return-await
    try {
      const res = await unref(formElRef)?.validate(nameList)
      return transFormFieldsValue(res)
    } catch (error) {
      return Promise.reject(error)
    }
  }

  async function clearValidate(name?: string | string[]) {
    await unref(formElRef)?.clearValidate(name)
  }

  async function scrollToField(name: NamePath, options?: ScrollOptions | undefined) {
    await unref(formElRef)?.scrollToField(name, options)
  }

  /**
   * @description: Form submission
   */
  async function handleSubmit(e?: Event): Promise<void> {
    e && e.preventDefault()
    const { submitFunc } = unref(getProps)
    if (submitFunc && isFunction(submitFunc)) {
      await submitFunc()
      return
    }
    const formEl = unref(formElRef)
    if (!formEl) return
    try {
      const values = await validate()
      const res = handleFormValues(values)
      emit('submit', res)
    } catch (error: any) {
      throw new Error(error)
    }
  }

  return {
    handleSubmit,
    clearValidate,
    validate,
    validateFields,
    getFieldsValue,
    updateSchema,
    resetSchema,
    appendSchemaByField,
    removeSchemaByFiled,
    resetFields,
    setFieldsValue,
    scrollToField,
  }
}
